import { useEffect, useRef } from 'react';
import useEditorialMotion from '@/hooks/useEditorialMotion';
import { scrollToTop } from '@/lib/smoothScroll';
import { useSearchParams } from 'react-router-dom';
import SiteNav from '@/components/feature/SiteNav';
import Footer from '@/pages/home/components/Footer';
import CareFaq from '@/pages/shop/components/CareFaq';
import { useBag } from '@/components/feature/bag/BagContext';
import { productCategories, products } from '@/mocks/products';

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const { openProduct } = useBag();
  const raw = params.get('category') ?? 'all';
  const active = productCategories.some((c) => c.id === raw) ? raw : 'all';
  const list = active === 'all' ? products : products.filter((p) => p.category === active);
  const ref = useRef<HTMLElement>(null);
  useEditorialMotion(ref, [active]);

  useEffect(() => {
    const prev = document.title;
    document.title = 'The Flower Edit — Shop | Fleur & Form';
    scrollToTop();
    return () => {
      document.title = prev;
    };
  }, []);

  const select = (id: string) => {
    setParams(id === 'all' ? {} : { category: id }, { replace: false });
  };

  return (
    <main className="ff-sans min-h-screen bg-white [overflow-x:clip] text-[#111111]">
      <SiteNav />
      <section ref={ref} className="px-4 md:px-10 pt-12 md:pt-24 pb-20 md:pb-36">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8 md:mb-12">
          <div>
            <h1 data-m-head className="font-normal tracking-[-0.045em] leading-none text-[44px] md:text-[88px]">The Flower Edit</h1>
            <p data-m-fade data-m-at="0.15" className="ff-serif italic text-[16px] md:text-[18px] text-black/60 mt-3 md:mt-4">Objects of nature, composed by hand.</p>
          </div>
          <span className="text-[11px] md:text-[12px] text-black/40 tabular-nums">{list.length} pieces</span>
        </div>

        <div className="flex flex-wrap gap-x-6 gap-y-2 border-b border-black/10 pb-4 mb-10 md:mb-16" role="tablist" aria-label="Categories">
          {productCategories.map((c) => (
            <button
              key={c.id}
              type="button"
              role="tab"
              aria-selected={active === c.id}
              onClick={() => select(c.id)}
              className={`text-[13px] whitespace-nowrap cursor-pointer pb-0.5 border-b transition-colors ${active === c.id ? 'border-black text-black' : 'border-transparent text-black/45 hover:text-black'}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div data-m-par-scope className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12 md:gap-y-20">
          {list.map((p, i) => (
            <div key={p.id} className="min-w-0" data-m-par={[30, 84, 36][i % 3]} data-m-par-t={[10, 26][i % 2]} data-m-scrub="0.9">
            <button type="button" onClick={() => openProduct(p.id)} data-m-in="up" data-m-dist="44" data-m-at={(i % 3) * 0.1} className="group w-full text-left flex flex-col min-w-0 cursor-pointer">
              <div className="w-full aspect-[4/5] overflow-hidden">
                <img src={p.image} width={1000} height={1250} loading={i < 3 ? 'eager' : 'lazy'} decoding="async" alt={`${p.name} — Fleur & Form`} title={`${p.name} — Fleur & Form`} className="w-full h-full object-cover object-center transition-transform duration-[420ms] ease-out group-hover:scale-[1.025]" />
              </div>
              <div className="flex items-baseline justify-between gap-3 pt-3 md:pt-4">
                <div className="flex items-baseline gap-3 min-w-0">
                  <span className="text-[11px] text-black/40">{p.no}</span>
                  <span className="text-[16px] md:text-[18px] tracking-[-0.02em] truncate">{p.name}</span>
                </div>
                <span className="text-[13px] whitespace-nowrap">${p.price}</span>
              </div>
            </button>
            </div>
          ))}
        </div>
      </section>
      <CareFaq />
      <Footer />
    </main>
  );
}